/**
 * PIFster Vote Intent Controller
 *
 * Thin bootstrap for vote-intent orchestration and modal wiring.
 */

import { resolveApiBase } from './vote-intent-api';
import { initModal, type VoteIntentModal } from './vote-intent-view';
import { bindMigrationPrompt } from './vote-intent/migration-prompt';
import { createVoteStateService } from './vote-intent/vote-state-service';
import { scrapeVoteContext, type VoteDomContextInput } from './vote-intent/vote-dom-scraper';
import { shouldVoteIntentYieldToFluentCart } from './fluentcart/fluentcart-readiness';
import { handleVoteIntent } from './vote-intent/vote-intent-orchestrator';
import { submitMagicLink } from './vote-intent/vote-intent-submit';
import { redirectToLogin } from './utils/auth-routing';
import { isValidEmail } from './utils/validation';
import type { PostId, VoteContext } from '../types/pifster-contracts';

type PifsterWindow = Window & {
  PIFSTER_DEBUG?: boolean;
  PIFSTER_DEBUG_FORCE_VOTE_EMAIL_MODAL?: boolean;
  PIFSTER?: {
    readonly debug?: boolean;
    readonly loginUrl?: string;
    readonly voteSuccessUrl?: string;
  };
};

interface VoteIntentOpenDetail {
  readonly charityId?: string;
  readonly charityName?: string;
  readonly periodId?: string;
  readonly periodLabel?: string;
}

interface VoteIntentModalContext {
  readonly charityId?: string | number;
  readonly charityName?: string;
  readonly periodId?: string | number;
  readonly periodLabel?: string;
}

function dlog(...args: unknown[]): void {
  try {
    const enabled = (window as PifsterWindow).PIFSTER_DEBUG === true || (window as PifsterWindow).PIFSTER?.debug === true;
    if (!enabled) {
      return;
    }

    if (window.console && typeof window.console.debug === 'function') {
      window.console.debug('[PIF vote][controller]', ...args);
    }
  } catch {
    // no-op
  }
}

function buildReturnStatusUrl(statusCode: string, charityId: PostId, periodId: PostId): string {
  const base = (window as PifsterWindow).PIFSTER?.voteSuccessUrl ?? `${window.location.origin}/return-status/`;
  const params = new URLSearchParams();
  params.set('action', 'vote');
  params.set('status', statusCode);
  params.set('charity_id', String(charityId));
  params.set('period_id', String(periodId));
  return `${base.replace(/\/$/, '/')}${base.includes('?') ? '&' : '?'}${params.toString()}`;
}

/**
 * Ensure the Turnstile widget renders after the modal becomes visible.
 *
 * turnstile-gate.js renders widgets on DOMContentLoaded, but the vote modal
 * is hidden at that point so the widget may fail silently. We directly trigger
 * rendering by clearing the rendered flag and dispatching a custom event that
 * turnstile-gate.js can listen for.
 */
function ensureTurnstileWidget(container: HTMLElement | null): void {
  if (!container) {
    return;
  }

  // Already has a valid token — nothing to do.
  if (container.dataset['turnstileToken']) {
    return;
  }

  // Clear the "rendered" flag so turnstile-gate treats it as new.
  delete container.dataset['turnstileRendered'];

  // Dispatch a custom event to trigger re-render
  container.dispatchEvent(new CustomEvent('pifster:turnstile-rerender', { bubbles: true }));
}

function openVoteModal(modal: VoteIntentModal, context?: VoteIntentModalContext): void {
  modal.reset();

  const charityEl = modal.modal.querySelector<HTMLElement>('#pif-vote-intent-charity');
  const periodEl = modal.modal.querySelector<HTMLElement>('#pif-vote-intent-period');
  const charityId = context?.charityId ? String(context.charityId) : '';
  const periodId = context?.periodId ? String(context.periodId) : '';
  const charityName = context?.charityName ?? '';
  const periodLabel = context?.periodLabel ?? '';

  if (modal.base.charityInput) {
    modal.base.charityInput.value = charityId;
  }
  if (modal.base.periodInput) {
    modal.base.periodInput.value = periodId;
  }
  if (modal.base.sendButton) {
    modal.base.sendButton.disabled = true;
  }
  if (charityEl) {
    charityEl.textContent = charityName ? `You're voting for ${charityName}` : "You're voting…";
  }
  if (periodEl) {
    periodEl.textContent = periodLabel || 'for this month';
  }

  modal.modal.hidden = false;
  const dialog = modal.modal.querySelector<HTMLElement>('.c-modal__dialog');
  if (dialog) {
    dialog.setAttribute('tabindex', '-1');
    dialog.focus();
  }
  document.body.style.overflow = 'hidden';

  // Trigger Turnstile widget render now that the modal is visible.
  ensureTurnstileWidget(modal.base.turnstileContainer);
}

function closeVoteModal(modal: VoteIntentModal): void {
  if (modal.modal.hidden) {
    return;
  }

  modal.reset();
  modal.modal.hidden = true;
  document.body.style.overflow = '';
}

function bindBaseEvents(modal: VoteIntentModal, pifsterWindow: PifsterWindow): void {
  const { emailInput, sendButton, charityInput, periodInput, loginLink } = modal.base;

  if (emailInput) {
    emailInput.addEventListener('input', () => {
      const valid = isValidEmail(emailInput.value || '');
      if (sendButton) {
        sendButton.disabled = !valid;
      }
      if (!valid) {
        modal.setMode('base');
      }
    });
  }

  if (sendButton) {
    sendButton.addEventListener('click', (event: MouseEvent) => {
      event.preventDefault();
      void submitMagicLink(modal, {
        email: emailInput ? emailInput.value : '',
        charityId: charityInput ? charityInput.value : '',
        periodId: periodInput ? periodInput.value : '',
        triggerButton: sendButton,
        statusHandler: modal.setBaseStatus,
        onSuccess: (currentModal, email) => {
          currentModal.setSuccessEmail(email);
          currentModal.setMode('success');
        },
      });
    });
  }

  if (loginLink) {
    loginLink.addEventListener('click', (event: MouseEvent) => {
      event.preventDefault();
      redirectToLogin({
        email: emailInput ? emailInput.value : '',
        loginUrl: pifsterWindow.PIFSTER?.loginUrl,
      });
    });
  }
}

function bindSuccessEvents(modal: VoteIntentModal, pifsterWindow: PifsterWindow): void {
  if (modal.success.resendButton) {
    modal.success.resendButton.addEventListener('click', (event: MouseEvent) => {
      event.preventDefault();
      modal.setSuccessStatus('If an account exists, we\'ve resent the link.');
    });
  }

  if (modal.success.loginLink) {
    modal.success.loginLink.addEventListener('click', (event: MouseEvent) => {
      event.preventDefault();
      redirectToLogin({
        email: modal.base.emailInput ? modal.base.emailInput.value : '',
        loginUrl: pifsterWindow.PIFSTER?.loginUrl,
      });
    });
  }
}

function bindModalEvents(modal: VoteIntentModal): void {
  modal.modal.addEventListener('click', (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    if (target.dataset['close']) {
      closeVoteModal(modal);
    }
  });

  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape' && !modal.modal.hidden) {
      closeVoteModal(modal);
    }
  });
}

function bindOpenVoteIntentEvents(modal: VoteIntentModal): void {
  document.addEventListener('pifster:open-vote-intent', (event: Event) => {
    if (!(event instanceof CustomEvent)) {
      return;
    }

    const detail = event.detail as VoteIntentOpenDetail | undefined;
    openVoteModal(modal, detail);
  });
}

export function initVoteIntentController(): void {
  const pifsterWindow = window as PifsterWindow;
  const modal = initModal(document.getElementById('pif-vote-intent-modal'));
  if (!modal) {
    dlog('modal init failed');
    return;
  }

  bindModalEvents(modal);
  bindOpenVoteIntentEvents(modal);
  bindBaseEvents(modal, pifsterWindow);
  bindSuccessEvents(modal, pifsterWindow);

  if (pifsterWindow.PIFSTER_DEBUG_FORCE_VOTE_EMAIL_MODAL === true) {
    modal.modal.hidden = false;
    bindMigrationPrompt();
    return;
  }

  const stateService = createVoteStateService();
  bindMigrationPrompt();

  document.addEventListener('click', async (event: MouseEvent) => {
    if (event.defaultPrevented) {
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    const button = target.closest('.js-vote-button, [data-pifster-vote]') as HTMLButtonElement | null;
    if (!button) {
      return;
    }

    if (shouldVoteIntentYieldToFluentCart(document.documentElement.dataset['pifsterFluentcartReady'])) {
      return;
    }

    if (button.dataset['pifsterFluentcartHandled'] === '1') {
      return;
    }

    event.preventDefault();
    if (button.disabled || button.dataset['clickLocked']) {
      return;
    }

    button.dataset['clickLocked'] = '1';
    button.setAttribute('aria-disabled', 'true');

    try {
      const scraped = scrapeVoteContext(button);
      const contextInput: VoteDomContextInput = {
        charityId: String(scraped.context.charityId),
        charityName: scraped.context.charityName,
        periodId: String(scraped.context.periodId),
        periodLabel: scraped.context.periodLabel,
        donateHref: scraped.donateHref,
      };

      await handleVoteIntent(
        {
          resolveApiBase,
          stateService,
          openVoteModal: (context: VoteContext) => {
            openVoteModal(modal, context);
          },
          redirectTo: (url: string) => {
            window.location.href = url;
          },
          buildReturnStatusUrl,
          getLoginUrl: () => pifsterWindow.PIFSTER?.loginUrl ?? `${window.location.origin}/wp-login.php`,
        },
        contextInput,
        undefined,
      );
    } finally {
      delete button.dataset['clickLocked'];
      button.removeAttribute('aria-disabled');
    }
  });

}

export {};
